import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const Meta = ({ description, lang, meta, image, title, robots, canonical, hreflang }) => {

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            image
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const metaImage = image || site.siteMetadata.image

  title = title || site.siteMetadata.title

  // Prepare meta tags in the correct order
  const metaTags = [
    {
      name: `description`,
      content: metaDescription,
    }
  ]

  // Add robots meta tag if provided (right after description)
  if (robots) {
    metaTags.push({
      name: 'robots',
      content: robots
    })
  }

  // Add Open Graph meta tags
  metaTags.push(
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:image`,
      content: metaImage,
    }
  )

  // Add Twitter meta tags
  metaTags.push(
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    }
  )

  // Add any additional meta tags
  metaTags.push(...meta)

  // Prepare link tags
  const linkTags = [
    {
      rel: `shortcut icon`,
      type: `image/x-icon`,
      href: `/favicon.ico`
    },
    {
      rel: `apple-touch-icon`,
      sizes: `180x180`,
      href: `/apple-touch-icon.png`
    },
    {
      rel: `apple-touch-icon`,
      type: `image/png`,
      sizes: `32x32`,
      href: `/favicon-32x32.png`
    },
    {
      rel: `apple-touch-icon`,
      type: `image/png`,
      sizes: `16x16`,
      href: `/favicon-16x16.png`
    }
  ]

  // Add canonical link if provided (before Open Graph tags)
  if (canonical) {
    linkTags.push({
      rel: 'canonical',
      href: canonical
    })
  }

  // Add hreflang links if provided (before Open Graph tags)
  if (hreflang && Array.isArray(hreflang)) {
    hreflang.forEach(link => {
      linkTags.push({
        rel: 'alternate',
        hreflang: link.hreflang,
        href: link.href
      })
    })
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={metaTags}
      link={linkTags}
    />
  )
}

Meta.defaultProps = {
  lang: `en`,
  meta: [],
  robots: null,
  canonical: null,
  hreflang: null,
}

Meta.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  title: PropTypes.string,
  robots: PropTypes.string,
  canonical: PropTypes.string,
  hreflang: PropTypes.arrayOf(PropTypes.shape({
    hreflang: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })),
}

export default Meta